import React from 'react';
import { twMerge } from 'tailwind-merge';
import type * as TF from 'type-fest';

import Box from '#/ui/components/box';
import type { Breakpoint } from '#/ui/types';
import type { OverrideProps } from '#/ui/utils/types';

export interface ContainerOwnProps {
  /**
   * Determine the max-width of the container. The container width grows with the size of the screen.
   * @default 'lg'
   */
  maxWidth?: Breakpoint;
  /**
   * If true, the left and right padding is removed.
   * @default false
   */
  disableGutters?: boolean;
  /**
   * The className to apply.
   */
  className?: string;
  /**
   * The content of the container.
   */
  children?: React.ReactNode;
}

export type ContainerTypeMap<
  AdditionalProps = TF.EmptyObject,
  RootComponent extends React.ElementType = 'div',
> = {
  props: AdditionalProps & ContainerOwnProps;
  defaultComponent: RootComponent;
};

export type ContainerProps<
  RootComponent extends React.ElementType = ContainerTypeMap['defaultComponent'],
  AdditionalProps = TF.EmptyObject,
  // @ts-expect-error
> = OverrideProps<ContainerTypeMap<AdditionalProps, RootComponent>, RootComponent> & {
  component?: React.ElementType;
};

/**
 * The container centers your content horizontally. It's the most basic layout element.
 */
function Container<
  C extends React.ElementType = ContainerTypeMap['defaultComponent'],
  P = ContainerTypeMap['props'],
>(
  {
    maxWidth = 'lg',
    disableGutters = false,
    className,
    children,
    component = 'div',
    ...props
  }: ContainerProps<C, P>,
  ref?: React.ForwardedRef<C>
) {
  const sizeClassname = React.useMemo(() => getSizeClassnames(maxWidth), [maxWidth]);
  return (
    <Box
      {...props}
      ref={ref}
      component={component}
      className={twMerge(
        'gooey-container w-full mx-auto',
        className,
        disableGutters ? 'px-0' : 'px-4 md:px-8',
        sizeClassname
      )}
    >
      {children}
    </Box>
  );
}
Container.displayName = 'GooeyContainer';

export default Container;

/*
|------------------
| Internals
|------------------
*/

const sizeMap = {
  xs: 'max-w-screen-xs',
  sm: 'max-w-screen-sm',
  md: 'max-w-screen-md',
  lg: 'max-w-screen-lg',
  xl: 'max-w-screen-xl',
  '2xl': 'max-w-screen-2xl',
} satisfies Record<Breakpoint, string>;

function getSizeClassnames(size: Breakpoint) {
  return sizeMap[size];
}
